<template>
  <div>
    <div class="tool-bar-wrapper">
      <div v-if="target === 'reply'">
        <div
          class="react-button"
          @click="toggleEditor"
        >
          <Tooltip
            content="Reply"
            placement="top"
          >
            <i class="far fa-comment" />
          </Tooltip>
        </div>
      </div>
      <div
        v-else-if="target === 'post'"
        style="display: flex; flex-direction: row;"
      >
        <div
          v-if="isAdmin"
          class="react-button"
        >
          <Tooltip
            :content="`Make it ${sticky ? 'non-' : ''}sticky`"
            placement="top"
          >
            <Checkbox
              v-model="sticky"
              @on-change="toggleStickyPost"
            />
          </Tooltip>
        </div>
        <div
          class="react-button"
          @click="scrollToEditor"
        >
          <Tooltip
            content="Scroll to Reply"
            placement="top"
          >
            <i class="far fa-comment" />
          </Tooltip>
        </div>
        <div class="react-button">
          <Tooltip
            content="Share Post"
            placement="top"
          >
            <i
              class="fas fa-share-alt"
              @click="onSharePostLink"
            />
          </Tooltip>
        </div>
      </div>
      <div
        class="react-button like"
        :class="likeClass(actionStatus.isLike)"
        @click="toggleAction('like')"
      >
        <Tooltip
          content="Like"
          placement="top"
        >
          <i :class="{ 'fas fa-thumbs-up': actionStatus.isLike, 'far fa-thumbs-up': !actionStatus.isLike }" />
        </Tooltip>
      </div>
      <span
        v-show="!actionStatus.likeCount"
        class="total-items"
      />
      <span
        v-show="actionStatus.likeCount"
        class="total-items"
      >
        {{ actionStatus.likeCount }}
      </span>
      <!-- <div
        class="react-button"
        :class="favoriteClass(actionStatus.isFavorite)"
        @click="toggleAction('favorite')"
      >
        <Tooltip
          content="Favorite"
          placement="top"
        >
          <i :class="{ 'fas fa-heart': actionStatus.isFavorite, 'far fa-heart': !actionStatus.isFavorite }" />
        </Tooltip>
      </div> -->
      <div
        v-if="(!postResolved || bestAnswer) && postAuthorId === userId && target === 'reply'"
        class="react-button"
        @click="onOpenBestAnswerModal"
      >
        <Tooltip
          :content="postResolved ? 'Remove Best Answer' : 'Mark as Best Answer'"
          placement="top"
        >
          <i :class="{ 'fas fa-check-circle': postResolved, 'far fa-check-circle': !postResolved }" />
        </Tooltip>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

const COMPONENT_REPLY = 'forum_reply'
const COMPONENT_POST = 'forum_post'

export default {
  inject: ['postActionStatus', 'repliesActionStatus'],

  props: {
    postId: Number,
    postSticky: {
      type: Boolean,
      default: false
    },
    replyId: {
      type: Number,
      default: null
    },
    target: {
      type: String,
      default: 'reply' // post | reply
    },
    actionStatus: {
      type: Object,
      default: () => ({
        isLike: false,
        isFavorite: false,
        likeCount: 0,
        likeId: null,
        favId: null
      })
    },
    isAdmin: {
      type: Boolean,
      default: false
    },
    userId: Number,
    postAuthorId: Number,
    postResolved: Boolean,
    bestAnswer: Boolean
  },

  data () {
    return {
      user: null,
      sticky: this.postSticky
    }
  },

  computed: {
    componentType () {
      return this.target === 'reply' ? COMPONENT_REPLY : COMPONENT_POST
    },

    componentId () {
      return this.target === 'reply' ? this.replyId : this.postId
    }
  },

  created () {
    this.user = this.$auth.getUser()
  },

  methods: {
    /**
     * Emit an event for parent component to hide/display the editor
     */
    toggleEditor () {
      this.$emit('toggle-editor')
    },

    /**
     * Emit an event for parent component to scroll down to the bottom
     */
    scrollToEditor () {
      this.$emit('scroll-to-editor')
    },

    /**
     * Displays a success message if the copy to clipboard was successful.
     */
    onCopySuccess () {
      this.$Message.info({
        content: 'Copied to clipboard!',
        duration: 2
      })
    },

    /**
     * Displays an error message if the copy to clipboard failed.
     */
    onCopyError () {
      this.$Message.error({
        content: 'Error while copying to clipboard!',
        duration: 2
      })
    },

    /**
     * Copies the post share link into the clipboard.
     */
    onSharePostLink () {
      const linkToShare = `${window.location.origin}${this.$route.path}`
      this.$copyText(linkToShare).then(
        this.onCopySuccess.bind(this),
        this.onCopyError.bind(this)
      )
    },

    /**
     * Fetch the status of like action
     * @return {Promise}
     */
    async fetchLikeStatus () {
      return axios.get(`${process.env.VUE_APP_API_URI}/users/${this.user.id}/like`, {
        params: {
          componentType: this.componentType,
          componentId: this.componentId
        }
      })
    },

    /**
     * Fetch the status of favorite action
     * @return {Promise}
     */
    async fetchFavStatus () {
      return axios.get(`${process.env.VUE_APP_API_URI}/users/${this.user.id}/favorite`, {
        params: {
          componentType: this.componentType,
          componentId: this.componentId
        }
      })
    },

    /**
     * Fetch the total likes numbers towards the post/reply
     * @return {Promise}
     */
    async fetchTotalLikes () {
      return axios.get(`${process.env.VUE_APP_API_URI}/user-actions/total-likes`, {
        params: {
          componentType: this.componentType,
          componentId: this.componentId
        }
      })
    },

    /**
     * Trigger favorite and unfavorite actions
     * @param {String} method
     * @param {Object} dataParams payload for post/delete reqeusts
     */
    async toggleFavorite (method, dataParams) {
      try {
        const result = await axios({
          method: method,
          url: `${process.env.VUE_APP_API_URI}/users/favorite`,
          headers: {
            'Content-Type': 'application/json'
          },
          data: dataParams
        })

        const updatedId = method === 'post' ? result.data[0].id : this.actionStatus.favId

        // Update root action status data locally: favId, isFavorite
        if (this.target === 'reply') {
          this.$set(this.repliesActionStatus[this.replyId], 'favId', updatedId)
          this.$set(this.repliesActionStatus[this.replyId], 'isFavorite', !this.actionStatus.isFavorite)
        } else {
          this.$set(this.postActionStatus, 'favId', updatedId)
          this.$set(this.postActionStatus, 'isFavorite', !this.actionStatus.isFavorite)
        }
      } catch (error) {
        console.error(`Error to toggle favorite: ${error}`)
      }
    },

    /**
     * Trigger like and unlike actions
     * @param {String} method
     * @param {Object} dataParams payload for post/delete reqeusts
     */
    async toggleLike (method, dataParams) {
      try {
        const result = await axios({
          method: method,
          url: `${process.env.VUE_APP_API_URI}/users/like`,
          headers: {
            'Content-Type': 'application/json'
          },
          data: dataParams
        })

        // Update root action status data locally: likeCount, likeId, isLike
        const updatedId = method === 'post' ? result.data[0].id : this.actionStatus.likeId
        const likeCountResult = await this.fetchTotalLikes()
        const updatedLikeCount = likeCountResult.data.length ? likeCountResult.data[0].total_likes : 0
        if (this.target === 'reply') {
          this.$set(this.repliesActionStatus[this.replyId], 'likeId', updatedId)
          this.$set(this.repliesActionStatus[this.replyId], 'isLike', !this.actionStatus.isLike)
          this.$set(this.repliesActionStatus[this.replyId], 'likeCount', updatedLikeCount)
          // update user scores, don't hold anything up, though
          axios.post(`${process.env.VUE_APP_API_URI}/forum/posts/${this.postId}/replies/${this.replyId}/like`, { method })
        } else {
          this.actionStatus.isLike ? this.$log('forum_unlike', dataParams.component_id, dataParams) : this.$log('forum_like', dataParams.component_id, dataParams)
          this.$set(this.postActionStatus, 'likeId', updatedId)
          this.$set(this.postActionStatus, 'isLike', !this.actionStatus.isLike)
          this.$set(this.postActionStatus, 'likeCount', updatedLikeCount)
          // update user scores, don't hold anything up, though
          axios.post(`${process.env.VUE_APP_API_URI}/forum/posts/${this.postId}/like`, { method })
        }
      } catch (error) {
        console.error(`Error to toggle like: ${error}`)
      }
    },

    /**
     * Trigger for either toggle favorite or toggle like
     * Given action type (like | favorite)
     * @param {String} action action type, either like or favorite
     */
    async toggleAction (action) {
      // Specify configuration data required for each toggle request
      const config = action === 'like' ? {
        flag: this.actionStatus.isLike,
        actionId: this.actionStatus.likeId
      } : {
        flag: this.actionStatus.isFavorite,
        actionId: this.actionStatus.favId
      }

      // Construct data params required for each toggle reqeust
      const dataParams = config.flag ? {
        id: config.actionId,
        componentType: this.componentType
      } : {
        userId: this.user.id,
        componentType: this.componentType,
        componentId: this.componentId
      }
      const method = config.flag ? 'delete' : 'post'

      await action === 'like' ? this.toggleLike(method, dataParams) : this.toggleFavorite(method, dataParams)
    },

    /**
     * Sets the class to a like icon according its status.
     * @param {Boolean} isLike
     */
    likeClass (isLike) {
      return isLike ? 'active' : ''
    },

    /**
     * Sets the class to a favorite icon according its status.
     * @param {Boolean} isFavorite
     */
    favoriteClass (isFavorite) {
      return isFavorite ? 'favorite' : ''
    },

    /**
     * Toggles the sticky behavior for a Post.
     */
    async toggleStickyPost () {
      await axios.patch(`${process.env.VUE_APP_API_URI}/forum/post/${this.postId}`, {
        sticky: this.sticky
      })
    },

    /**
     * Trigger the confirmation modal to mark/remove a best answer for the post
     */
    onOpenBestAnswerModal () {
      this.bestAnswer
        ? this.$Modal.confirm({
          title: 'Remove the Best Answer',
          content: 'Are you sure to remove the best answer?',
          closable: true,
          onOk: () => {
            this.$store.dispatch('forum/updateBestAnswer', { postId: this.postId, replyId: this.replyId, postResolved: this.postResolved })
            this.$emit('best-answer', false)
            this.$log('post_unresolve', this.postId, { uncheckReplyId: this.replyId })
          }
        }) : this.$Modal.confirm({
          title: 'Choose the Best Answer',
          content: 'Are you sure to mark this reply as the best answer?',
          closable: true,
          onOk: () => {
            this.$store.dispatch('forum/updateBestAnswer', { postId: this.postId, replyId: this.replyId, postResolved: this.postResolved })
            this.$emit('best-answer', true)
            this.$log('post_resolve', this.postId, { checkReplyId: this.replyId })
          }
        })
    }
  }
}
</script>

<style scoped>
.tool-bar-wrapper {
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  color: var(--color-text-title);
}

.react-button {
  margin-right: 8px;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  font-weight: bold;
  color: var(--color-black);
  opacity: .7;
  transition: opacity .3s ease-in;
}

.react-button:hover {
  opacity: 1;
}

.react-button.like {
  margin-right: 0;
}

.active {
  opacity: 1;
  color: var(--color-green-neon);
}

.favorite {
  opacity: 1;
  color: pink;
}

.total-items {
  color: var(--color-text-content);
  width: 10px;
}
</style>
